// MyTicketsPage.jsx — M3. The session account's own tickets from
//   GET /api/me/tickets?status&archived&role&limit&offset
// The list is server-isolated to the session account — as a PARTY: the submitter
// or, on an assigned ticket (ASK-, SB-190 §8), the responder; each row carries
// `role` and the responder side gets a chip. Rows are grouped by status
// (lifecycle order) and sorted by updatedAtMs descending within each group.
// Filters: a status select and an archived switch (both map to query params so
// the backend does the filtering). The switch reads like the approvals queue's
// (OQ-E167): off = live tickets only (archived=false), on = archived only.
import { Fragment, useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { pagingNotice } from '../api/paging.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import AttachmentPicker, { AttachmentNotesBlock } from '../components/AttachmentPicker.jsx';
import DataTable from '../components/DataTable.jsx';
import GroupChip from '../components/GroupChip.jsx';
import HelpLink from '../components/HelpLink.jsx';
import PolishButton from '../components/PolishButton.jsx';
import TicketDetailView, { fmtTime } from '../components/TicketDetailView.jsx';
import TicketStatusChip from '../components/TicketStatusChip.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import {
  MAX_ATTACHMENTS,
  attachHelperText,
  canAttach,
  doneCount,
  draftOf,
  failedItems,
  keepUnsent,
  mergeNoteRows,
  noteRows,
  orphanNoteItems,
  putDraftCount,
  putDraftFiles,
  putDraftNoteRows,
  remainingQuota,
  renderNotesBlock,
  sendableItems,
  unsentItems,
  uploadItems,
  withNotesBlock,
} from './attachments.mjs';
import { STATUS_ORDER, groupByStatus } from './ticketMeta.mjs';

// SB-056: the submitter-visible detail. The read-only block (body / feedback
// thread / attachments / timeline) lives in the shared TicketDetailView
// (extracted batch 50 for the approver surface); this wrapper contributes the
// me-family wiring and the info_requested reply box (T7) + withdraw (T8).
//
// 第五十六批新增：**建单之后的补传附件面**。此前这里只有一个备注框加两个按钮，
// 于是美术合理地推断"重新提交传不了附件"，撤回 AST-000008 重开 AST-000009 补 7 个文件。
// 后端（routes_me.cpp 的 attachment_window_open）一直允许 pending / info_requested 两态上传——
// 补的是界面的缺口，判据用 canAttach() 与后端逐格对齐。第三百六十二批：后端第三百五十九批起
// 多了一格「ASK 提出方在 assigned 时」，canAttach 现在要看类型、提出方与看客三样（见下）。
// 第五十七批 D2：files / attachCount **不在这个组件里**——它每上传成功一次就换 key 重挂
// （reloadSeq），行折叠又是 unmountOnExit，状态活不过一次重取。两者都由 MyTicketsPage
// 按工单号持有（attachments.mjs 的 drafts 表），这里只收 props。
//
// SB-190 §8（第二百四十五批）：指派单（ASK-）的两个动作也在这里——响应框只给受指派人
// （可多次，成一条反馈线程），关闭框只给发出方／审批人／管理员。P1：责任方永远不该控制
// 量它的那把尺子，所以受指派人的面板上**根本没有**关闭按钮（后端对他回 404）。
function TicketDetailPanel({
  no,
  status,
  archived,
  type,                 // SB-190：'assigned' 才有响应／关闭框；也是 ASK 附件窗的一格（canAttach）
  submitterId,          // SB-190：发出方（关闭权的一半；ASK 附件窗只开给它，第三百六十二批）
  assigneeId,           // SB-190：受指派人（响应权；且 P1 禁他关闭）
  notify,
  onChanged,            // 状态变了：折叠并重取列表（resubmit / withdraw / close）
  onRefresh,            // SB-190：只重取列表、**不折叠**（respond 之后行要留在眼前）
  files,
  onFilesChange,
  attachCount,          // 这张工单**已有**几个附件（null = 详情还没取回来）
  onAttachCount,
  noteRowsFor,          // 这张工单**本次会话里**已生成的说明行（页面级，见 drafts）
  onNoteRows,
}) {
  const t = useT();
  const { account } = useAuth();
  const me = Number(account?.id);
  const [note, setNote] = useState('');
  // SB-190：响应正文与关闭说明各自一格，不与 info_requested 的 note 共用——三个框
  // 互斥出现，但共用一个 state 意味着切换工单类型时旧文字会跑进另一种动作的请求体。
  const [responseBody, setResponseBody] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  // 第一百一十批：润色额度。**面板级**而不是页面级——PolishButton 的头注说额度该由
  // 页面持有，理由是"同一页上可能有两三个润色按钮共享一个池，各自去查会看到互相
  // 不一致的剩余数"。这一页不是那个形状：补充信息回复框每次只有一个（单开行 +
  // 折叠即卸载），所以池里只有一个消费者，跨面板共享反而要把状态提到一个与它无关
  // 的层。null 是合法初值（按钮照常可按，由后端 429 给权威答案）。
  const [polishQuota, setPolishQuota] = useState(null);
  // 写了说明却没能进清单的项数（文件没传上去）。**由 files 现算**，不另存一份 state：
  // 失败/被拒的项本来就留在 files 里（第五十六批的解药），而这个面板每传成功一次就
  // 换 key 重挂，任何本地 state 都活不过那一次（第五十七批 D2 的教训）。
  const orphans = orphanNoteItems(files).length;
  const [busy, setBusy] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  // TicketDetailView 自己按 [no, lang] 取数，外部推不动它；上传成功后换 key 重挂一次，
  // 新附件才会立刻出现在上面的清单里（否则用户看不到证据，又会怀疑没传上）。
  const [reloadSeq, setReloadSeq] = useState(0);
  // 第三百六十二批：ASK 的窗只给提出方（看客 me ＝ submitterId），所以三样都要传——只传
  // (status, archived) 的旧形状下 ASK 一律不开，提出方就又看不到框了（第三百五十九批的缺口）。
  const mayAttach = canAttach(status, archived, { type, submitterId, me });
  // 后端按整张工单已有的行数封顶（ticket_store.cpp:737），所以挑选器的上限是**剩余额度**。
  const remaining = remainingQuota(attachCount);
  // 第五十八批 C1：预校验没过的项永远上不去，按钮与"先传后重提"的判据都只能看**可送出**
  // 的那些——按 files.length 算的话，列表里只剩被拒项时点"上传"什么也不会发生，
  // 那种沉默比报错更难懂。
  const sendable = sendableItems(files).length;

  // 返回 { allOk, rows }：rows 是**这一趟真的传上去的**附件的说明行。
  // 失败的项留在挑选器里等重试——绝不清空。
  const runUpload = async () => {
    const out = await uploadItems(files, (f, note) => api.uploadTicketAttachment(no, f, note));
    const ok = doneCount(out);
    const bad = failedItems(out).length;
    // E1：说明行只能从上传结局里长出来（id / 落库文件名只有后端回执给得出）。
    // 先记账再清表单——keepUnsent 会把 done 项连同它们的回执一起清掉。
    const rows = noteRows(out);
    if (rows.length > 0) onNoteRows(rows);
    onFilesChange(keepUnsent(out));
    if (ok > 0) {
      notify(t('submit.attach.uploaded', { n: String(ok) }), 'success');
      // 剩余额度先按乐观值收窄，随后的重取会用真值覆盖：不这么做的话，重取回来之前
      // 用户还能按旧额度再挑满一批，最后几个照样被后端 400。
      onAttachCount((attachCount ?? 0) + ok);
      setReloadSeq((n) => n + 1);
    }
    if (bad > 0) notify(t('submit.attach.failed', { n: String(bad) }), 'error');
    return { allOk: bad === 0, rows };
  };

  const doUpload = async () => {
    if (sendable === 0) return;
    setBusy(true);
    try {
      await runUpload();
    } finally {
      setBusy(false);
    }
  };

  const doWithdraw = async () => {
    setBusy(true);
    try {
      await api.withdrawInfoTicket(no);
      notify(t('tickets.detail.withdraw.done'), 'success');
      setConfirmWithdraw(false);
      onChanged();
    } catch (e) {
      notify(t(errKey(e, { fallback: 'tickets.error' })), 'error');
    } finally {
      setBusy(false);
    }
  };

  const doResubmit = async () => {
    setBusy(true);
    try {
      // 顺序是**先传附件、后重新提交**：resubmit 成功会触发 onChanged()，面板随即卸载，
      // 挑好还没传的文件就跟着消失了。倒过来写等于当场丢附件。
      let rows = noteRowsFor;
      if (sendable > 0) {
        const r = await runUpload();
        if (!r.allOk) {
          // 有文件没上去就不提交：否则提交人以为材料齐了，处理方拿到的是残缺工单，
          // 而工单一旦离开 info_requested 也就不再有这个补传入口。
          notify(t('tickets.detail.attach.hold'), 'warning');
          return;
        }
        rows = mergeNoteRows(noteRowsFor, r.rows);
      }
      // 这一面是全站**唯一**"上传发生在一次文本写入之前"的地方：resubmit 的补充说明
      // 是 me 家族仅有的建单后文本写入口（routes_me.cpp 只有建单/传附件/补充回复/撤回
      // 四个写入口，没有改正文）。所以清单在这里能真正随工单落库，而且是按上传结局
      // 生成的——需求方要的那件事，在这条路径上是结构性成立的。
      // withNotesBlock 幂等：用户若把上一次的清单粘回了回复框，这里是"剥离→按附件 id
      // 归并→整块重写"，绝不会贴出两份互相矛盾的地面真值（要求 6）。
      const body = rows.length
        ? withNotesBlock(
            note.trim(),
            rows,
            t('submit.attach.notes.about', { n: String(rows.length) }),
          )
        : note.trim();
      await api.resubmitTicket(no, body || undefined);
      notify(t('tickets.detail.resubmit.done'), 'success');
      if (rows.length) notify(t('submit.attach.notes.inReply', { n: String(rows.length) }), 'success');
      setNote('');
      onChanged();
    } catch (e) {
      notify(t(errKey(e, { fallback: 'tickets.error' })), 'error');
    } finally {
      setBusy(false);
    }
  };

  // 补传面：pending 与 info_requested 都给，指派单则只给提出方、只在 assigned 时给（与后端
  // attachment_window_open 同一条件）。放在回复框**上方**——「先补材料、再重新提交」是自上而下
  // 读得出来的顺序。
  const attachBox = mayAttach ? (
    <>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('tickets.detail.attach.title')}
        </Typography>
        <AttachmentPicker
          items={files}
          onChange={onFilesChange}
          disabled={busy}
          /* 剩余额度 + 每单总额：一张已有 7 个附件的工单只该再收 3 个，而且要说明白。 */
          max={remaining}
          quotaTotal={attachCount == null ? null : MAX_ATTACHMENTS}
          /* 序号接着这张工单**已有**的附件往下数：补传面上写 #1 会和工单上的实际
             位置差一大截，而正文清单常常是按工单上的顺序写的。 */
          indexBase={attachCount ?? 0}
          label={null}
          /* 允许名单话术同样由 ATTACH_KINDS 表生成——这一面原来一个字都没提可传什么。 */
          helper={`${t('tickets.detail.attach.helper')} ${attachHelperText(t)}`}
        />
        <Box sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={busy || sendable === 0}
            onClick={doUpload}
          >
            {t('tickets.detail.attach.action')}
          </Button>
        </Box>
        {/* 已生成的说明清单。三种归宿，话术必须分开——说错一边就是许下做不到的承诺：
            · info_requested：下面的「重新提交」是建单后唯一的文本写入口，清单会随它
              一起落库，用户什么都不用做。
            · pending：这张工单此刻**没有**任何文本写入口（正文只读、resubmit 只在
              info_requested 可用）。所以只能把清单交到手上，并说清为什么。
            · 指派单（第三百六十二批）：没有「请补信息」也没有给提出方的回复框，pendingLocked
              那句里的两样都不存在；说明已挂在附件上，补传不单独通知受指派人，首答之后窗关。 */}
        <AttachmentNotesBlock
          block={renderNotesBlock(
            noteRowsFor,
            t('submit.attach.notes.about', { n: String(noteRowsFor.length) }),
          )}
          rows={noteRowsFor.length}
          orphans={orphans}
          note={
            status === 'info_requested'
              ? t('submit.attach.notes.willRide', { n: String(noteRowsFor.length) })
              : type === 'assigned'
                ? t('submit.attach.notes.askIssuer')
                : t('submit.attach.notes.pendingLocked')
          }
          onNotify={notify}
        />
      </Box>
    </>
  ) : null;

  const replyBox =
    status === 'info_requested' ? (
      <>
        <Divider />
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('tickets.detail.resubmit.title')}
          </Typography>
          <TextField
            multiline
            minRows={2}
            fullWidth
            size="small"
            label={t('tickets.detail.resubmit.label')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {/* 第一百一十批：润色接上 `reply` 场景。后端 scene、守门（fail-closed：
              润色稿丢了原文的编号/路径/URL 就整次作废且不扣额）、额度、i18n 全都
              从 SB-067 一期起就现成——**当年只漏了这一个按钮**，于是这一格
              （提交人回应"请补充信息"的那一格，全站最需要把话说清楚的一格）
              是三个润色场景里唯一没有它的。 */}
          <PolishButton
            value={note}
            scene="reply"
            onChange={setNote}
            quota={polishQuota}
            onQuota={setPolishQuota}
            disabled={busy}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button variant="contained" size="small" disabled={busy} onClick={doResubmit}>
              {t('tickets.detail.resubmit.action')}
            </Button>
            {/* T8: give up instead of answering — terminal, so confirmed. */}
            <Button
              variant="outlined"
              color="warning"
              size="small"
              disabled={busy}
              onClick={() => setConfirmWithdraw(true)}
            >
              {t('tickets.detail.withdraw.action')}
            </Button>
          </Stack>
          <Dialog open={confirmWithdraw} onClose={() => setConfirmWithdraw(false)}>
            <DialogTitle>{t('tickets.detail.withdraw.title')}</DialogTitle>
            <DialogContent>
              <Typography variant="body2">{t('tickets.detail.withdraw.desc')}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmWithdraw(false)}>{t('common.cancel')}</Button>
              <Button color="warning" disabled={busy} onClick={doWithdraw}>
                {t('tickets.detail.withdraw.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </>
    ) : null;

  // ── SB-190 §8：指派单的响应（A1）与关闭（A2）────────────────────────────────
  // 判据与后端逐格对齐（routes_me.cpp 的两条路由）：
  //   · 响应：受指派人本人，且单子还开着（assigned | responded）。
  //   · 关闭：发出方 ∥ approve 位 ∥ 管理员——**且不是受指派人**（P1）。一个既是受指派人
  //     又有 approve 位的账号在这里看不到按钮；他真去打接口，store 也会抛。
  // 已归档的单两者都不给：归档只认 closed（is_terminal），开着的单归不了档，反之亦然。
  const isAsk = type === 'assigned' && status !== 'closed' && !archived;
  const mayRespond = isAsk && Number(assigneeId) === me;
  const mayClose = isAsk
    && (Number(submitterId) === me || Boolean(account?.mayApprove) || Boolean(account?.isAdmin))
    && Number(assigneeId) !== me;   // P1: the responder has no close button

  const doRespond = async () => {
    const body = responseBody.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.respondTicket(no, body);
      notify(t('tickets.detail.respond.done'), 'success');
      setResponseBody('');
      // **不折叠**（不调 onChanged）：响应可以重复，受指派人要看着线程继续写；
      // 换 key 重挂一次让新反馈立刻出现在上面的清单里（与上传附件同一条路）。
      setReloadSeq((n) => n + 1);
      onRefresh();
    } catch (e) {
      notify(t(errKey(e, { fallback: 'tickets.error' })), 'error');
    } finally {
      setBusy(false);
    }
  };

  const doClose = async () => {
    setBusy(true);
    try {
      await api.closeTicket(no, closeNote.trim() || undefined);
      notify(t('tickets.detail.close.done'), 'success');
      setConfirmClose(false);
      setCloseNote('');
      onChanged();
    } catch (e) {
      notify(t(errKey(e, { fallback: 'tickets.error' })), 'error');
    } finally {
      setBusy(false);
    }
  };

  const respondBox = mayRespond ? (
    <>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('tickets.detail.respond.title')}
        </Typography>
        <TextField
          multiline
          minRows={3}
          fullWidth
          size="small"
          label={t('tickets.detail.respond.label')}
          value={responseBody}
          onChange={(e) => setResponseBody(e.target.value)}
        />
        {/* 同 replyBox：这一格是被量的那个人说话的地方，润色照给。 */}
        <PolishButton
          value={responseBody}
          scene="reply"
          onChange={setResponseBody}
          quota={polishQuota}
          onQuota={setPolishQuota}
          disabled={busy}
        />
        <Box sx={{ mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            disabled={busy || !responseBody.trim()}
            onClick={doRespond}
          >
            {t('tickets.detail.respond.action')}
          </Button>
        </Box>
      </Box>
    </>
  ) : null;

  const closeBox = mayClose ? (
    <>
      <Divider />
      <Box>
        {/* A2 是这类工单唯一的出口、不可恢复——与 T8 撤回同样要确认。 */}
        <Button
          variant="outlined"
          color="warning"
          size="small"
          disabled={busy}
          onClick={() => setConfirmClose(true)}
        >
          {t('tickets.detail.close.action')}
        </Button>
        <Dialog open={confirmClose} onClose={() => setConfirmClose(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('tickets.detail.close.title')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>{t('tickets.detail.close.desc')}</Typography>
            <TextField
              multiline
              minRows={2}
              fullWidth
              size="small"
              label={t('tickets.detail.close.noteLabel')}
              value={closeNote}
              onChange={(e) => setCloseNote(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmClose(false)}>{t('common.cancel')}</Button>
            <Button color="warning" disabled={busy} onClick={doClose}>
              {t('tickets.detail.close.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  ) : null;

  return (
    <TicketDetailView
      key={reloadSeq}
      no={no}
      load={() => api.ticketDetail(no)}
      attachmentUrl={api.ticketAttachmentUrl}
      /* SB-190：这一面现在有两种看客（list_by_party），「我」跟着看客走——
         发出方看线程：submitter='我'、assignee='受指派人'；
         受指派人看线程：submitter='发出方'、assignee='我'。
         此前 submitter 恒为「我」是对的（只有提交人开得了面板）；受指派人进来之后，
         发出方 A2 的关闭说明（author_kind='submitter'）会在受指派人眼里标成「我」——
         承接方把发出方的话读成自己的话，而这一面是 D4 的证据面（第二百四十五批复审）。 */
      authorLabels={{
        submitter: Number(submitterId) === me ? 'tickets.detail.author.me' : 'tickets.detail.author.issuer',
        handler: 'tickets.detail.author.handler',
        assignee: Number(assigneeId) === me ? 'tickets.detail.author.me' : 'tickets.detail.author.assignee',
      }}
      /* 已有附件数只有详情接口给得出（列表行没有这个字段），取回来就往页面级草稿表里记，
         重挂一次也不会退回"以为还能再传 10 个"。 */
      onData={(d) => onAttachCount(Array.isArray(d?.attachments) ? d.attachments.length : 0)}
      footer={
        <>
          {attachBox}
          {replyBox}
          {respondBox}
          {closeBox}
        </>
      }
    />
  );
}

export default function MyTicketsPage() {
  const t = useT();
  const { lang } = useLang();
  const { snack, notify, clearSnack } = useNotify();

  const [statusFilter, setStatusFilter] = useState('');
  const [archived, setArchived] = useState(false);
  // SB-056: which row is expanded (ticket no or null). Single-open keeps the
  // grouped tables readable; the panel unmounts on collapse (the detail refetches
  // on reopen) — but the picked-but-unsent files do NOT die with it, see `drafts`.
  const [openNo, setOpenNo] = useState(null);

  // 第五十七批 D2：补传草稿（挑好还没传的文件 + 这张工单已有的附件数）按工单号存在**页面**上。
  // 详情面板会被重挂（上传后刷新清单）、会被卸载（折叠 unmountOnExit），状态放在它里面
  // 就等于"部分失败之后，一次慢的重取把待重试的文件抹掉"——本批要根治的正是这种丢文件。
  // 放在这里，重取、折叠、甚至详情接口报错都动不了它。
  const [drafts, setDrafts] = useState({});
  const setDraftFiles = useCallback(
    (no, files) => setDrafts((d) => putDraftFiles(d, no, files)),
    [],
  );
  // putDraftCount 同值返回原引用——onData 的 effect 会反复调用它，换新引用就是死循环。
  const setDraftCount = useCallback(
    (no, count) => setDrafts((d) => putDraftCount(d, no, count)),
    [],
  );
  // 说明行同样要活过面板重挂（上传成功 ⇒ 换 key）——它恰恰是在那一刻算出来的。
  const addDraftNoteRows = useCallback(
    (no, rows) => setDrafts((d) => putDraftNoteRows(d, no, rows)),
    [],
  );

  const list = useLoad(
    () =>
      api.myTickets({
        status: statusFilter || undefined,
        // OQ-E167（第三百八十二批，需求方裁）：与审批浏览同一读法——关＝只列未归档、开＝只列
        // 已归档。此前关着时不带参数，服务端不按归档筛，已归档的单照样混在列表里。
        archived: archived ? 'true' : 'false',
      }),
    // `lang` kept as a trigger: the pre-SB-042 effect refetched on language
    // toggle (its deps included `t`); dropping it would let the list go stale.
    [statusFilter, archived, lang],
  );
  const tickets = Array.isArray(list.data?.tickets) ? list.data.tickets : [];
  // SB-159（第三百八十二批）：悬崖护栏。这一页不带 limit，服务端缺省就是「全取」（刻意不设缺省上限），
  // 所以今天它是全的；但哪天服务端加了上限，页面会**静静变短**——审批浏览那一幕。服务端从这一批起
  // 回 total：张数与它对不上就说出来（「服务端共 M 张、此处 N 张」）。旧后端没有 total ⇒ 不说话。
  // 复审（N7）：这一页的那句是 tickets.paging.drift（不分页、不给页长 ⇒ 只会走到 drift）——它**不承诺**
  // 重试能补齐：对不上可能是加载途中有变动（重试能补），也可能是服务端加了上限（重试补不回来）。
  const notice = list.status === 'ready'
    ? pagingNotice({ rows: tickets, total: list.data?.total, capped: false,
                     legacy: typeof list.data?.total !== 'number' }, { ns: 'tickets.paging' })
    : null;

  // Group by status (lifecycle order), sort each group by updatedAtMs desc.
  const groups = groupByStatus(tickets);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 2 }}
      >
        <Typography sx={{ fontWeight: 700 }} variant="h5" component="h1">
          {t('tickets.title')}
          <HelpLink section="lifecycle" />
        </Typography>
        {/* useFlexGap：Stack 的 spacing 默认走 margin，折行后第二行会贴死且带 16px 假缩进 */}
        <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={2} useFlexGap>
          <TextField
            select
            size="small"
            label={t('tickets.filter.status')}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">{t('tickets.filter.all')}</MenuItem>
            {STATUS_ORDER.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`ticketStatus.${s}`)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={archived} onChange={(e) => setArchived(e.target.checked)} />}
            label={t('tickets.filter.archived')}
          />
        </Stack>
      </Stack>

      {notice ? (
        <Alert
          severity={notice.severity}
          data-paging-notice={notice.key}
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={list.reload}>
              {t('common.retry')}
            </Button>
          )}
        >
          {t(notice.key, notice.params)}
        </Alert>
      ) : null}

      {list.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : list.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(list.error, { fallback: 'tickets.error' }))}
          </Alert>
          <Button size="small" onClick={list.reload}>
            {t('common.retry')}
          </Button>
        </Box>
      ) : tickets.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {statusFilter || archived ? t('tickets.emptyFiltered') : t('tickets.empty')}
          </Typography>
          <Button variant="contained" component={RouterLink} to="/submit">
            {t('tickets.action.submitNew')}
          </Button>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {groups.map(({ status, rows }) => (
            <Box key={status}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                <TicketStatusChip status={status} />
                <Typography variant="body2" color="text.secondary">
                  {t('tickets.count', { n: rows.length })}
                </Typography>
              </Stack>
              <DataTable minWidth={600}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('tickets.col.no')}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('tickets.col.type')}</TableCell>
                      <TableCell>{t('tickets.col.title')}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('tickets.col.updated')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <Fragment key={row.no}>
                        <TableRow
                          hover
                          onClick={() => setOpenNo(openNo === row.no ? null : row.no)}
                          sx={{ cursor: 'pointer', '& > td': { borderBottom: 'none' } }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace' }}>
                            {row.no}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {t(`ticketType.${row.type}`) === `ticketType.${row.type}`
                              ? row.type
                              : t(`ticketType.${row.type}`)}
                          </TableCell>
                          <TableCell>
                            {/* flexWrap：窄视口下 Chip 掉到标题下一行，而不是把标题挤成竖塔 */}
                            <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={1} useFlexGap>
                              <span>{row.title}</span>
                              {/* 第一百零三批：组徽标一律显示（GroupChip 头注）——
                                  提交人跨组提单后，「哪张单在哪个组」不该靠记忆。 */}
                              <GroupChip groupId={row.projectGroupId} />
                              {/* SB-190 §8：这张单是**指派给我**的（我是响应方，不是提交人）。
                                  列表本就两侧都列（list_by_party），徽标是这一行唯一能
                                  说出"我在这张单上欠一个答复"的地方。 */}
                              {row.role === 'responder' ? (
                                <Chip size="small" color="warning" variant="outlined" label={t('tickets.roleResponder')} />
                              ) : null}
                              {row.archived ? (
                                <Chip size="small" variant="outlined" label={t('tickets.archivedChip')} />
                              ) : null}
                              {/* 折叠起来的行也要交代还有几个附件没传上去：文件确实被保留了
                                  （草稿在页面级），但"保留"如果看不见，跟丢了没有区别。 */}
                              {unsentItems(draftOf(drafts, row.no).files).length > 0 ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  label={t('tickets.detail.attach.pending', {
                                    n: String(unsentItems(draftOf(drafts, row.no).files).length),
                                  })}
                                />
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(row.updatedAtMs, lang)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 0 }}>
                            {/* sticky+maxWidth：表格有了 minWidth 之后，这个 colSpan 行会被撑到
                                表宽——详情面板钉在滚动窗口左缘并按视口收窄，读正文不用来回平移
                                （64px = 页面 Container 左右 16×2 + 单元格左右 16×2） */}
                            <Box sx={{ position: 'sticky', left: 0, maxWidth: 'calc(100vw - 64px)' }}>
                            <Collapse in={openNo === row.no} timeout="auto" unmountOnExit>
                              <TicketDetailPanel
                                no={row.no}
                                status={row.status}
                                archived={row.archived}
                                type={row.type}
                                submitterId={row.submitterId}
                                assigneeId={row.assigneeId}
                                notify={notify}
                                onRefresh={list.reload}
                                files={draftOf(drafts, row.no).files}
                                onFilesChange={(f) => setDraftFiles(row.no, f)}
                                attachCount={draftOf(drafts, row.no).attachCount}
                                onAttachCount={(n) => setDraftCount(row.no, n)}
                                noteRowsFor={draftOf(drafts, row.no).noteRows}
                                onNoteRows={(rows) => addDraftNoteRows(row.no, rows)}
                                onChanged={() => {
                                  setOpenNo(null);
                                  list.reload();
                                }}
                              />
                            </Collapse>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))}
                  </TableBody>
              </DataTable>
            </Box>
          ))}
        </Stack>
      )}
      <AppSnackbar snack={snack} onClose={clearSnack} autoHideDuration={5000} />
    </Box>
  );
}
